/**
 * CL-CART-01 Task 12 — CartCheckoutPage data loading and pricing.
 *
 * Two separate jobs, kept in separate slices of state because they fail independently and a
 * buyer needs to see WHICH one broke:
 *   - loading the seller's items, for display (title, image, unit price)
 *   - pricing the basket for a destination country, for the shipping figure and the total
 *
 * Both read the cart the same way CartPage.duck.js does: via selectCart from cart.duck, never
 * from a URL parameter or a prop — the basket lives in the buyer's own storage.
 */

import { createSlice } from '@reduxjs/toolkit';
import { createImageVariantConfig } from '../../util/sdkLoader';
import { denormalisedResponseEntities } from '../../util/data';
import { storableError } from '../../util/errors';
import { selectCart, clearSellerCart } from '../../ducks/cart.duck';
import { getSellerGroup } from '../../util/custom/cart';
import { allowedDeliveryCountriesForBasket } from '../../util/custom/shippingCheckout';
import { cartLineItems, cartInitiate, cartRelease } from '../../util/api';
import {
  SELLER_PAYOUT_INCOMPLETE,
  isSellerPayoutIncompleteApiError,
} from '../../util/custom/sellerPayoutErrors';

const initialState = {
  fetchListingsInProgress: false,
  fetchListingsError: null,
  listingsById: {},
  pricing: {
    fetchInProgress: false,
    fetchError: null,
    shippingAmountInSubunits: null,
    payinTotalInSubunits: null,
  },
  payment: {
    inProgress: false,
    error: null,
    transaction: null,
  },
};

const slice = createSlice({
  name: 'CartCheckoutPage',
  initialState,
  reducers: {
    fetchListingsRequest: state => {
      state.fetchListingsInProgress = true;
      state.fetchListingsError = null;
    },
    fetchListingsSuccess: (state, action) => {
      state.fetchListingsInProgress = false;
      state.listingsById = action.payload;
    },
    fetchListingsError: (state, action) => {
      state.fetchListingsInProgress = false;
      state.fetchListingsError = action.payload;
    },
    fetchPricingRequest: state => {
      state.pricing.fetchInProgress = true;
      state.pricing.fetchError = null;
    },
    fetchPricingSuccess: (state, action) => {
      state.pricing.fetchInProgress = false;
      state.pricing.shippingAmountInSubunits = action.payload.shippingAmountInSubunits;
      state.pricing.payinTotalInSubunits = action.payload.payinTotalInSubunits;
    },
    fetchPricingError: (state, action) => {
      state.pricing.fetchInProgress = false;
      state.pricing.fetchError = action.payload;
      // A failed re-price must not leave the PREVIOUS country's figure on screen looking
      // current — AC-18 is about a stale figure being shown as final just as much as a
      // missing one.
      state.pricing.shippingAmountInSubunits = null;
      state.pricing.payinTotalInSubunits = null;
    },
    initiateRequest: state => {
      state.payment.inProgress = true;
      state.payment.error = null;
    },
    initiateSuccess: (state, action) => {
      state.payment.inProgress = false;
      state.payment.transaction = action.payload;
    },
    initiateError: (state, action) => {
      state.payment.inProgress = false;
      state.payment.error = action.payload;
    },
  },
});

export const {
  fetchListingsRequest,
  fetchListingsSuccess,
  fetchListingsError,
  fetchPricingRequest,
  fetchPricingSuccess,
  fetchPricingError,
  initiateRequest,
  initiateSuccess,
  initiateError,
} = slice.actions;

export default slice.reducer;

// ================ Selectors ================ //

/**
 * The one seller's group, joined with live listing data, in the shape OrderItemList and the
 * page's own totals expect. Never from the cart's stored snapshots — same rule as the cart
 * page: a price the buyer is shown must be the current one.
 */
export const selectSellerGroup = (state, authorId) => {
  const group = getSellerGroup(selectCart(state), authorId);
  const listingsById = state.CartCheckoutPage.listingsById;

  const items = (group?.items || []).map(cartItem => {
    const listing = listingsById[cartItem.listingId];
    const unitPriceInSubunits = Number.isInteger(listing?.attributes?.price?.amount)
      ? listing.attributes.price.amount
      : null;
    const currency = listing?.attributes?.price?.currency || null;
    const quantity = cartItem.quantity || 1;
    return {
      listingId: cartItem.listingId,
      title: listing?.attributes?.title || cartItem.titleSnapshot || null,
      quantity,
      unitPriceInSubunits,
      currency,
      lineTotalInSubunits: unitPriceInSubunits == null ? null : unitPriceInSubunits * quantity,
      imageUrl: listing?.images?.[0]?.attributes?.variants?.['listing-card']?.url || null,
    };
  });

  const currency = items.find(i => i.currency)?.currency || null;
  const itemSubtotalInSubunits = items.every(i => Number.isInteger(i.lineTotalInSubunits))
    ? items.reduce((sum, i) => sum + i.lineTotalInSubunits, 0)
    : null;

  // D7, for a basket. The single-item checkout restricts its country select to what the listing
  // serves; without the same guard here a buyer can pick a country the server will refuse, and
  // the only feedback is a 400 when they press Pay.
  const allowedDeliveryCountries = allowedDeliveryCountriesForBasket(
    (group?.items || []).map(i => listingsById[i.listingId]?.attributes?.publicData).filter(Boolean)
  );

  return {
    sellerName: group?.authorDisplayName || null,
    items,
    itemSubtotalInSubunits,
    currency,
    allowedDeliveryCountries,
  };
};

export const selectPricing = state => state.CartCheckoutPage.pricing;
export const selectPayment = state => state.CartCheckoutPage.payment;

// ================ Thunks ================ //

/**
 * Fetch this one seller's listings (author, images, currentStock), for display. Scoped to a
 * single seller's items, unlike CartPage's fetch — this page never shows another seller's
 * rows (section M: one payment covers one seller).
 */
export const fetchCheckoutListings = authorId => (dispatch, getState, sdk) => {
  const cart = selectCart(getState());
  const group = getSellerGroup(cart, authorId);
  const ids = (group?.items || []).map(i => i.listingId);

  if (ids.length === 0) {
    dispatch(fetchListingsSuccess({}));
    return Promise.resolve({});
  }

  dispatch(fetchListingsRequest());

  return sdk.listings
    .query({
      ids,
      include: ['author', 'images', 'currentStock'],
      'fields.image': ['variants.listing-card', 'variants.listing-card-2x'],
      // `listing-card` is not a built-in Sharetribe variant — it is a NAMED CROP this app
      // defines per request (see ListingPage.duck.js, the working reference for this exact
      // shape). Asking for `fields.image: 'variants.listing-card'` without also DEFINING
      // that name here gets back an image resource with no `attributes` at all: not an
      // error, just silently empty. Confirmed against a real Dev response — the included
      // image was `{ id, type }` with nothing else.
      ...createImageVariantConfig('listing-card', 400, 0.75),
      ...createImageVariantConfig('listing-card-2x', 800, 0.75),
    })
    .then(response => {
      const listings = denormalisedResponseEntities(response);
      const byId = Object.fromEntries(listings.map(l => [l.id.uuid, l]));
      dispatch(fetchListingsSuccess(byId));
      return byId;
    })
    .catch(e => {
      dispatch(fetchListingsError(storableError(e)));
    });
};

/**
 * Price the basket for a destination country (D-5). Calls the server — never computed in the
 * browser — because the shipping formula (Task 8) and the commission it feeds (Task 9) both
 * depend on the seller's live listing data, which only the server has just fetched (AC-32).
 */
export const fetchPricing = ({ authorId, destinationCountry }) => (dispatch, getState) => {
  const cart = selectCart(getState());
  const group = getSellerGroup(cart, authorId);
  const items = group?.items || [];
  if (items.length === 0) return Promise.resolve();

  dispatch(fetchPricingRequest());

  return cartLineItems({
    cartItems: items.map(i => ({ listingId: i.listingId, quantity: i.quantity || 1 })),
    destinationCountry,
    // No `anchorListingId`. The server picks the anchor itself (the first validated item) and
    // ignores anything sent here — passing one implied the browser had a say in which listing
    // the transaction hangs off, which is exactly the kind of thing AC-32 says it must not.
  })
    .then(response => {
      const lineItems = response.data || [];
      const shippingLine = lineItems.find(li => li.code === 'line-item/shipping-fee');
      const shippingAmountInSubunits = shippingLine ? shippingLine.lineTotal.amount : 0;

      // What the card will actually be charged, summed the way the template's own
      // `calculateTotalForCustomer` does it: every line the CUSTOMER is included for.
      //
      // NOT `itemSubtotal + shipping`. A customer commission is "added on top of the order
      // price to get the customer's payin price" (lineItemHelpers.js), so re-deriving the
      // total from two lines promises less than Stripe takes the moment one is configured in
      // Console — and a cart discount (Milestone C) misses in the other direction. The server
      // already answers with every line; there is no reason to guess from a subset.
      const payinTotalInSubunits = lineItems
        .filter(li => (li.includeFor || []).includes('customer'))
        .reduce((sum, li) => sum + li.lineTotal.amount, 0);

      dispatch(fetchPricingSuccess({ shippingAmountInSubunits, payinTotalInSubunits }));
      return { shippingAmountInSubunits, payinTotalInSubunits };
    })
    .catch(e => {
      dispatch(fetchPricingError(storableError(e)));
    });
};

/**
 * Start a real, payable transaction (Step 3a's endpoint).
 *
 * Sends only listing ids, quantities, the destination country and the delivery address —
 * nothing about money. Prices, line items, commission and the stock hold are all decided
 * server-side (AC-32), so a tampered request cannot change what is charged.
 *
 * The error is normalised to a `code` here rather than in the component, because the component
 * has to tell a CARD failure from a SOLD-OUT one and show different wording for each (AC-24,
 * R7) — putting that mapping in the view would spread transport details through the UI.
 */
export const submitCartPayment = ({
  authorId,
  destinationCountry,
  shippingDetails,
  stripe,
  card,
  savedPaymentMethodId = null,
  billingDetails,
}) => (dispatch, getState, sdk) => {
  const cart = selectCart(getState());
  const group = getSellerGroup(cart, authorId);
  const items = group?.items || [];
  if (items.length === 0) return Promise.resolve();

  // Checked before cart-initiate, not before the charge: initiating HOLDS the basket's stock
  // and creates a real order, so discovering a missing card afterwards would lock the buyer's
  // own items behind an order that can never be paid.
  //
  // A SAVED card needs no card element — Stripe is given the payment method's id — so only one
  // of the two has to be present.
  if (!stripe || !(card || savedPaymentMethodId)) {
    return Promise.resolve(dispatch(initiateError({ code: 'card-not-ready' })));
  }

  dispatch(initiateRequest());

  // Set once cart-initiate succeeds. From that moment the basket's non-anchor stock is held by
  // hand, and EVERY later failure has to give it back (R4) — Sharetribe's own expire-payment
  // releases the anchor and knows nothing about the rest.
  let transactionId = null;

  const releaseIfHeld = () =>
    transactionId ? cartRelease({ transactionId }).catch(() => null) : Promise.resolve();

  // Resolves to null, never to the error action. The page routes to the order on a truthy
  // result, so "did this work?" stays a plain check rather than an inspection of whatever the
  // last step happened to return.
  const fail = error =>
    releaseIfHeld().then(() => {
      dispatch(initiateError(error));
      return null;
    });

  return (
    cartInitiate({
      cartItems: items.map(i => ({ listingId: i.listingId, quantity: i.quantity || 1 })),
      destinationCountry,
      shippingDetails,
    })
      // ---- Step 2: Stripe charges the card ----
      .then(response => {
        const tx = response?.data?.data;
        transactionId = tx?.id?.uuid || null;

        const clientSecret =
          tx?.attributes?.protectedData?.stripePaymentIntents?.default
            ?.stripePaymentIntentClientSecret;

        // Without this, Stripe is called with `undefined` and answers with an opaque complaint
        // about a malformed secret — which reads as a card problem when it is actually a
        // transaction that came back wrong.
        if (!clientSecret) {
          const e = new Error('missing-payment-intent');
          e.cartCode = 'missing-payment-intent';
          throw e;
        }

        // A saved card is confirmed by id; a typed one by the element plus billing details.
        const paymentMethod = savedPaymentMethodId
          ? savedPaymentMethodId
          : { billing_details: billingDetails, card };

        return stripe
          .confirmCardPayment(clientSecret, { payment_method: paymentMethod })
          .then(result => {
            // Stripe.js RESOLVES with `{ error }` rather than rejecting. Without this the
            // sequence walks straight past a charge that never happened into confirm-payment
            // — telling Sharetribe money arrived when it did not. Sharetribe catches it
            // (`stripe-invalid-payment-intent-status`), but by then the failure reads as a
            // basket problem instead of the plain card decline it was.
            if (result?.error) throw result.error;
            return result;
          });
      })
      // ---- Step 3: tell Sharetribe the payment went through ----
      //
      // Not privileged, so it goes straight through the SDK — the same call the single-item
      // checkout makes. The line items were already fixed server-side at initiate.
      .then(() =>
        sdk.transactions.transition(
          {
            id: transactionId,
            transition: 'transition/confirm-payment',
            params: {},
          },
          { include: ['booking', 'provider'], expand: true }
        )
      )
      .then(response => {
        dispatch(initiateSuccess(response?.data?.data || null));
        // Brief step 10. Only HERE, once the payment is actually complete — not after
        // cart-initiate. A declined card must leave the basket untouched so the buyer can try
        // another one (E11), and clearing earlier would destroy it on every decline.
        //
        // Only this seller's group: one payment covers one seller (section M), so another
        // seller's items are still waiting to be bought and must survive.
        //
        // Without this the buyer's own purchase comes back to them on the cart's next
        // availability check as "No longer available — this one has sold", which reads as
        // somebody else having taken it.
        dispatch(clearSellerCart({ authorId }));
        // The transaction itself, so the caller can route to it without digging through the
        // SDK response shape.
        return response?.data?.data || null;
      })
      .catch(e => {
        // Three shapes of failure reach here, and the buyer needs different wording for each:
        //   - our endpoint's 409, naming the item that sold (R7)
        //   - Stripe's own rejection, which carries { error: { code } }
        //   - anything else
        //
        // `e.data.errors` is the shape `handleError` and `sendCartError` both produce, and the
        // one src/util/api.js's `Object.assign(new Error(), await res.json())` unpacks. The
        // cart endpoints used to send these as TRANSIT, which that JSON parse turned into an
        // error carrying keys '0','1','2' — so this read `undefined` and every lost-item
        // failure below collapsed to `errorGeneric`. See server/api-util/custom/cartHttp.js.
        const apiErrors = e?.data?.errors || e?.errors || [];
        const first = Array.isArray(apiErrors) ? apiErrors[0] : null;
        // A Stripe error arrives bare — it is thrown out of `confirmCardPayment`'s resolved
        // `{ error }` above — and is recognised by `type` ('card_error', 'validation_error',
        // …), which no error of ours carries. The `e.error` form is kept for a rejection,
        // which Stripe.js does not do today but which its docs do not rule out.
        const stripeError = e?.error || (e?.type ? e : null);
        const stripeCode = stripeError?.code;

        // CL-CHK-01: a seller whose Stripe onboarding was started but never finished is refused
        // with the generic payment-failed code; only the row's Stripe message tells it apart.
        const error = isSellerPayoutIncompleteApiError(first)
          ? { code: SELLER_PAYOUT_INCOMPLETE }
          : first
          ? { code: first.code, listingId: first.listingId }
          : stripeCode
          ? // Stripe uses card_declined / card_error / expired_card and more. They are all the
            // same thing to a buyer: the card did not work, the basket is intact, try again.
            { code: 'card-declined', stripeCode }
          : { code: e?.cartCode || 'unknown' };

        return fail(error);
      })
  );
};

/**
 * Nothing to load on the server: the cart lives in browser storage, so the server does not
 * know what is in it (same reasoning as CartPage.loadData). The listing fetch happens on the
 * client once the cart duck has initialised — see CartCheckoutPage.js.
 */
export const loadData = () => () => Promise.resolve();
